// Raising the on-screen keyboard from JavaScript.
//
// iOS Safari and Chrome for Android only open the soft keyboard when
// `.focus()` runs inside the task of the user gesture that asked for it.
// A focus call that lands even a tick later still moves the caret, but
// the keyboard stays down — which is why tapping the header's search
// icon used to drop you into the overlay with a focused-but-silent
// field that needed a second tap.
//
// The overlay can't focus its own input inside that window: SearchPopup
// is a lazy chunk in the headers, and even where it isn't, React has to
// mount it before the input exists. So bridge the gap — focus a
// throwaway input synchronously in the tap handler (the keyboard comes
// up against that), then hand focus over to the real input once it
// mounts. Moving focus between two text fields while the keyboard is
// already up keeps it up, so the user lands on the search field ready
// to type.

let primer: HTMLInputElement | null = null;

// Pointer coarseness is the closest proxy the platform gives us for
// "typing here means an on-screen keyboard". Desktop is left alone —
// nothing primed, no stray node in the DOM.
export const hasSoftKeyboard = (): boolean => typeof window !== 'undefined'
  && typeof window.matchMedia === 'function'
  && window.matchMedia('(pointer: coarse)').matches;

// Takes the node to drop explicitly, so a blur arriving late for a
// superseded primer can't tear down the one that replaced it.
const dropPrimer = (node: HTMLInputElement | null = primer) => {
  if (!node) return;
  if (primer === node) primer = null;
  // Deferred: tearing the node out while the browser is still settling
  // focus away from it can bounce focus back to <body> on WebKit, and
  // the keyboard down with it. By the next task the handover is done.
  setTimeout(() => node.remove(), 0);
};

/**
 * Call synchronously from a tap/click handler that is about to open a
 * search overlay. No-op on devices without a soft keyboard.
 */
export const primeSoftKeyboard = (): void => {
  if (!hasSoftKeyboard()) return;
  dropPrimer();

  const input = document.createElement('input');
  input.type = 'text';
  input.tabIndex = -1;
  input.setAttribute('aria-hidden', 'true');
  // It has to stay rendered and focusable, so `display: none` and
  // `visibility: hidden` are both out. A transparent 1px box pinned to
  // the viewport is invisible and never scrolls the page on focus; the
  // 16px font size keeps iOS from zooming the viewport into it.
  input.style.cssText = [
    'position:fixed',
    'top:0',
    'left:0',
    'width:1px',
    'height:1px',
    'padding:0',
    'border:0',
    'opacity:0',
    'font-size:16px',
  ].join(';');
  // If the overlay never arrives — a chunk that fails to load, a tap the
  // user backs out of — the primer must not linger holding focus.
  input.addEventListener('blur', () => dropPrimer(input), { once: true });

  document.body.appendChild(input);
  primer = input;
  input.focus();
};

/**
 * Focus the real search input, taking the keyboard over from the primer
 * if one is up. Safe to call when nothing was primed.
 */
export const focusWithSoftKeyboard = (el: HTMLElement | null | undefined): void => {
  el?.focus();
  dropPrimer();
};
